import { UpsertCountryDto, UpsertPatientDocumentDto } from 'ts-types/api.types';
import { MessageSourceProps, withMessages } from 'react-message-source';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { addPatientDocument, editPatientDocument } from 'services/patientDocumentServices';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { required } from 'utils/validatorUtils';
import FormContainer from 'components/layout/FormContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Button, Grid } from 'semantic-ui-react';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import styled from 'styled-components';
import FormContent from 'components/layout/FormContent';

const UpsertPatientDocumentFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  patientDocumentId?: number,
  patientDocument?: UpsertCountryDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertPatientDocumentForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<UpsertPatientDocumentDto>) => {

    let saveValues: Partial<UpsertPatientDocumentDto> = {...values};

    const {patientDocumentId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general"),
        cancelTokenSource
      })
    };

    if (patientDocumentId) {
      await editPatientDocument(patientDocumentId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addPatientDocument(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  initialPatientDocumentValues = (): Partial<UpsertPatientDocumentDto> => {
    return {
      fastPrint: false
    };
  };

  render(): React.ReactNode {

    const {patientDocument = this.initialPatientDocumentValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={patientDocument}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertPatientDocumentFormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("patientDocument.name")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="name"
                        component={Input}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("patientDocument.code")}
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="code"
                        component={Input}
                        validate={required}
                        disabled
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        name="fastPrint"
                        component={CheckBox}
                        label={`${getMessage("patientDocument.fastPrint")}`}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpsertPatientDocumentFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertPatientDocumentForm);
